import React from 'react';
import styled from 'styled-components';
import { Colors, Breakpoints } from '@style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import Link from '@core/PageSwitch/Link';
import Site from '@client/store/Site';
import Functions from '@client/style/Functions';
import Image from '../Image';
import SoMeIcon from '../SoMeIcon';
import { SquareButton } from '../shared/SquareButton';
import WaveDivider from '../Divider/WaveDivider';

const FooterContainer = styled.div<{ $bg?: string; $fg?: string }>`
	background-color: ${props => props.$bg || Colors.footerDarkBackground};
	color: ${props => props.$fg || Colors.lightText};
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: stretch;
	position: relative;
	z-index: 1;
	overflow: hidden;
	${TextStyle.family('title')}
	padding-left: var(--gridMargin);
	padding-right: var(--gridMargin);
	padding-top: 90px;
	margin-top: -5px;
	flex: 1 0 auto;
`;

const RowContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	padding-bottom: 50px;

	${Functions.breakpoint(Breakpoints.mobile)} {
		padding-bottom: 0px;
	}

	${Functions.breakpoint(Breakpoints.laptop)} {
		flex-direction: row;
		align-items: stretch;
		padding-bottom: 300px;
		padding-right: ${Functions.col(1, 16, 0.25)};
	}
`;

const ColumnContainer = styled.div`
	width: 100%;
	max-width: 600px;
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

const DesktopLinkList = styled(ColumnContainer)`
	text-align: center;

	${Functions.breakpoint(Breakpoints.laptop)} {
		justify-content: start;
		text-align: left;

		a:nth-of-type(4) {
			padding-top: 60px;
		}
	}
`;

const DesktopLink = styled(Link)`
	&:hover {
		color: ${Colors.newsBackground};
	}

	display: block;
	padding-bottom: 15px;
	${TextStyle.size('large')}
	line-height: 1.2;

	${Functions.breakpoint(Breakpoints.mobile)} {
		${TextStyle.size('p4')}
		line-height: 1.2;
	}
`;

const Sponsors = styled.div`
	display: flex;
	flex-direction: row;
	gap: var(--gridGutter);
	position: relative;
	margin-top: 50px;

	${Functions.breakpoint(Breakpoints.laptop)} {
		margin-top: 0px;
	}
`;

const SponsorImage = styled(Image)`
	width: 100%;
	height: unset;
	padding: 0px;

	image {
		object-fit: cover;
	}

	${Functions.breakpoint(Breakpoints.laptop)} {
		border-radius: 20px;
	}
`;

const SponsorButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
`;

const MoreSponsors = styled(SquareButton)`
	margin: 50px 0px;
	padding-left: 27px;
	padding-right: 27px;
`;

const FooterSoMeBar = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-wrap: wrap;
	flex-direction: row;
	padding-bottom: 80px;
	z-index: 1;
`;

const StyledLink = styled(Link)`
	width: 72px;
	height: 72px;
	display: grid;
	place-items: center;

	${Functions.breakpoint(Breakpoints.mobile)} {
		width: 120px;
		height: 120px;
	}

	svg {
		width: 48px;
		height: 48px;

		${Functions.breakpoint(Breakpoints.mobile)} {
			width: 61px;
			height: 61px;
		}
	}

	svg path {
		fill: currentColor;
	}
`;

const AllRights = styled.div`
	text-align: center;
	${TextStyle.family('title')}
	${TextStyle.size('large')}
	padding-bottom: 50px;
	color: ${Colors.lightText};
	z-index: 1;
	${Functions.breakpoint(Breakpoints.mobile)} {
		padding-bottom: 100px;
		${TextStyle.size('small')}
	}
`;

export default function Footer() {
	const footer = Site.use(state => state.footer);

	return (
		<>
			<WaveDivider fg={footer.backgroundColor?.color} />
			<FooterContainer
				className='footer colorWrapperAnimatedModule'
				$bg={footer.backgroundColor?.color}
				$fg={footer.backgroundColor?.contrastColor}
			>
				<RowContainer>
					<DesktopLinkList>
						{footer.desktopLinkList.map((link, i) => (
							<DesktopLink key={i} data={link}>
								{link.title}
							</DesktopLink>
						))}
					</DesktopLinkList>
					<ColumnContainer>
						<Sponsors>
							{footer.sponsors.map((image, i) => (
								<SponsorImage data={image} lossless={true} key={i}></SponsorImage>
							))}
						</Sponsors>
						<SponsorButtonContainer>
							<MoreSponsors link={footer.sponsorLink[0]} colors={footer.backgroundColor} invert={true}>
								{footer.sponsorLink[0]?.title}
							</MoreSponsors>
						</SponsorButtonContainer>
					</ColumnContainer>
				</RowContainer>
				<FooterSoMeBar>
					<>
						{footer.socialMediaBar.map((entry, index) => {
							return (
								<StyledLink key={index} data={{ link: entry.url }}>
									<SoMeIcon icon={entry.socialMediaChannel} />
								</StyledLink>
							);
						})}
					</>
				</FooterSoMeBar>
				<AllRights>Smukfest - All rights reserved</AllRights>
			</FooterContainer>
		</>
	);
}
